import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Webhook } from "lucide-react";
import { useAuth, isDeveloper } from "@/contexts/AuthContext";
import RigeneraSlugPanel from "@/components/RigeneraSlugPanel";
import ChiaviApiSection from "@/components/ChiaviApiSection";

// Sprint API-PUBBLICA.1 — gestione chiave API pubblica + webhook (per azienda).
export default function PublicApiSection({ companyId }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [emailComm, setEmailComm] = useState("");
  const [telComm, setTelComm] = useState("");

  const load = () => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/public-api`).then((r) => {
      setStatus(r.data);
      setWebhookUrl(r.data.webhook_url || "");
      setEmailComm(r.data.email_commerciale || "");
      setTelComm(r.data.telefono_commerciale || "");
    }).catch(() => {});
  };
  useEffect(load, [companyId]);

  const saveContatti = async () => {
    try {
      await api.put(`/companies/${companyId}/public-api/contatti`, { email_commerciale: emailComm || null, telefono_commerciale: telComm || null });
      toast.success("Contatti commerciali salvati"); load();
    } catch { toast.error("Errore salvataggio contatti"); }
  };

  const saveWebhook = async () => {
    try {
      await api.put(`/companies/${companyId}/public-api/webhook`, { webhook_url: webhookUrl || null, webhook_secret: webhookSecret || undefined });
      toast.success("Webhook salvato"); setWebhookSecret(""); load();
    } catch { toast.error("Errore salvataggio webhook"); }
  };

  return (
    <Card className="p-6 border border-slate-200 shadow-sm bg-white mt-4" data-testid="public-api-section">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound className="h-5 w-5 text-blue-600" />
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-900">API pubblica (sito web)</h3>
          <p className="text-sm text-slate-500">Chiave di sola lettura per alimentare il sito. Salvata in forma hashata.</p>
        </div>
      </div>

      {/* Sprint CHIAVI-1 — campo vecchio di compatibilità in SOLA LETTURA (niente Genera/Rigenera/Revoca) */}
      <div className="p-3 rounded-lg border border-slate-100 bg-slate-50 mb-5 text-sm" data-testid="public-api-legacy">
        {status?.has_key ? (
          <span className="text-slate-700" data-testid="public-api-legacy-stato">Chiave di compatibilità presente · termina in <code className="font-mono">…{status.last4}</code>
            {status.created_at && <span className="text-slate-400"> · dal {new Date(status.created_at).toLocaleDateString("it-IT")}</span>}
          </span>
        ) : (
          <span className="text-slate-400" data-testid="public-api-legacy-stato">Nessuna chiave di compatibilità</span>
        )}
        <div className="text-xs text-slate-500 mt-1">Le chiavi si gestiscono ora nell'elenco «Chiavi API» qui sotto.</div>
      </div>

      <ChiaviApiSection companyId={companyId} />

      <div className="flex items-center gap-2 mb-3">
        <Webhook className="h-4 w-4 text-slate-500" />
        <h4 className="font-medium text-sm text-slate-800">Webhook di invalidazione</h4>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-600">URL webhook</Label>
          <Input value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)}
                 placeholder="https://equocar.it/api/revalidate" data-testid="public-api-webhook-url" />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Secret {status?.has_webhook_secret && <span className="text-slate-400">(impostato)</span>}</Label>
          <Input value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)} type="password"
                 placeholder={status?.has_webhook_secret ? "•••••• (lascia vuoto per non cambiare)" : "secret condiviso"} data-testid="public-api-webhook-secret" />
        </div>
      </div>
      <div className="flex justify-end mt-3">
        <Button size="sm" onClick={saveWebhook} data-testid="public-api-webhook-save">Salva webhook</Button>
      </div>
      <p className="text-xs text-slate-400 mt-2">KARGEST notifica questo URL quando un veicolo pubblicato cambia (invio asincrono, non bloccante).</p>

      <div className="flex items-center gap-2 mb-3 mt-6 pt-5 border-t border-slate-100">
        <KeyRound className="h-4 w-4 text-slate-500" />
        <h4 className="font-medium text-sm text-slate-800">Contatti commerciali (annunci)</h4>
      </div>
      <p className="text-xs text-slate-400 mb-2">Distinti dai contatti amministrativi dei dati anagrafici. Se vuoti, l'API usa quelli amministrativi come fallback.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-600">Email commerciale</Label>
          <Input value={emailComm} onChange={(e) => setEmailComm(e.target.value)}
                 placeholder="vendite@azienda.it" data-testid="public-api-email-comm" />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Telefono commerciale</Label>
          <Input value={telComm} onChange={(e) => setTelComm(e.target.value)}
                 placeholder="+39 011 000000" data-testid="public-api-tel-comm" />
        </div>
      </div>
      <div className="flex justify-end mt-3">
        <Button size="sm" onClick={saveContatti} data-testid="public-api-contatti-save">Salva contatti</Button>
      </div>
      {isDeveloper(user) && <RigeneraSlugPanel companyId={companyId} />}
    </Card>
  );
}
